import { lightTheme } from '@/customTheme'
import { SCENE_THUMBNAIL_MIN_HEIGHT, SCENE_THUMBNAIL_MAX_HEIGHT } from '@/scenes/Editor/components/Scenes/sceneThumbnailUtils'
import React, { CSSProperties, useState } from 'react'
import emptyFramBG from '@assets/images/empty_frame_bg.svg'
import GradientLoader from '@/components/GradientLoader'

type ContentState = 'loading' | 'preview' | 'placeholder'

interface ThumbnailContentProps {
  preview?: string | null
  videoUrl?: string | null
  isDisabled?: boolean
  isLoading?: boolean
  /** Shows the GradientLoader as an overlay on top of the existing image/video. */
  loadingOverlay?: boolean
  baseSize: {
    width: number
    height: number
  }
  draggable?: boolean
  onImageLoad?: React.ReactEventHandler<HTMLImageElement>
  onVideoLoad?: React.ReactEventHandler<HTMLVideoElement>
}

const ThumbnailContent: React.FC<ThumbnailContentProps> = ({
  preview,
  videoUrl,
  isDisabled = false,
  isLoading = false,
  loadingOverlay = false,
  baseSize,
  draggable,
  onImageLoad,
  onVideoLoad,
}) => {
  const [videoReady, setVideoReady] = useState(false)
  const contentState: ContentState = isLoading ? 'loading' : (videoUrl || preview) ? 'preview' : 'placeholder'

  const baseImageStyles: CSSProperties = baseSize.height === SCENE_THUMBNAIL_MAX_HEIGHT ? {
    height: '100%',
    width: 'fit-content',
    borderRadius: '8px',
  } : baseSize.height === SCENE_THUMBNAIL_MIN_HEIGHT ? {
    width: '100%',
    height: 'fit-content',
    borderRadius: '8px',
  } : {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
  }

  const contentMap: Record<ContentState, React.ReactNode> = {
    loading: (
      <GradientLoader
        angle={0}
        top={0}
        left={0}
        width={baseSize.width}
        height={baseSize.height}
        borderRadius={8}
        showPercentage={false}
        style={{ position: 'relative', overflow: 'hidden' }}
      />
    ),
    preview: videoUrl ? (
      <>
        {!videoReady && preview && (
          <img
            src={preview}
            style={{ ...baseImageStyles, display: 'block', boxSizing: 'border-box', position: 'absolute' }}
            alt=""
          />
        )}
        <video
          src={videoUrl}
          style={{ ...baseImageStyles, display: 'block', boxSizing: 'border-box', opacity: videoReady ? 1 : 0 }}
          autoPlay
          loop
          muted
          playsInline
          controls={false}
          onCanPlay={() => setVideoReady(true)}
          onLoadedMetadata={onVideoLoad}
        />
      </>
    ) : (
      <img
        src={preview ?? undefined}
        style={{
          ...baseImageStyles,
          display: 'block',
          boxSizing: 'border-box',
          backgroundImage: `url(${emptyFramBG})`,
        }}
        alt=""
        draggable={draggable}
        onLoad={onImageLoad}
      />
    ),
    placeholder: (
      <div style={{
        width: '100%',
        height: '100%',
        borderRadius: '8px',
        background: lightTheme.colors.grayScale100,
      }} />
    ),
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        opacity: isDisabled ? 0.5 : 1,
        position: 'relative',
      }}
    >
      {contentMap[contentState]}
      {loadingOverlay && (
        <div style={{ position: 'absolute', inset: 0, borderRadius: '8px', overflow: 'hidden' }}>
          <GradientLoader
            angle={0}
            top={0}
            left={0}
            width={baseSize.width}
            height={baseSize.height}
            borderRadius={8}
            showPercentage={false}
            style={{ position: 'relative', overflow: 'hidden' }}
          />
        </div>
      )}
    </div>
  )
}

export default ThumbnailContent
